/* eslint-disable no-unused-vars */
import { useState, useEffect, useMemo, ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
// import "moment/locale/fr";
import PunctualCourseRepository from "../../Repositories/PunctualCourseRepository";
import { store } from "../../Redux/store";
import { HeadCell } from "../../Objects/interfaces/TableTypes";
import { capitalizeFirstLetter } from "../../Utils/Function";
import {
  getActiveClientBrandConfig,
  getActivePrestationBrandConfig,
} from "../../config/environment.config";
import {
  COURSE_TYPE_CONFIG,
  REDUX_TYPE_MAP,
  CourseType,
} from "../../config/COURSE_TYPE_CONFIG";
import { PunctualCourseDashboard } from "../../Objects/models/PunctualCourseDashboard";
import { SHOW_ERROR, SHOW_SUCCESS } from "../../Redux/Reducers/MessageReducers";
import { DELETE_COURSE } from "../../Redux/Actions/PunctualCourseActions";
import { isVerifiedTrainer, vtTtcFromNet } from "../../Utils/VerifiedTrainerAmounts";
import { OptionsProps } from "./CourseRowActions";

export interface UseListCourseParams {
  type: CourseType;
  limit?: number;
}

export function useListCourse({ type, limit }: UseListCourseParams) {
  const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
  const course = useSelector((state: any) => state);
  const dispatch = useDispatch();
  const config = COURSE_TYPE_CONFIG[type];
  const teachrId: number | undefined = teachr?.id;

  // ── Pagination ────────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ── Filtres texte / statut ────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [deleteCourse, setDeleteCourse] = useState(false);
  const [nameSearch, setNameSearch] = useState("");
  const [statusSearch, setStatusSearch] = useState("");

  // ── Filtre par période ────────────────────────────────────────────────────
  const [dateValues, setDateValues] = useState<any[]>([]);
  const [startDateSearch, setStartDateSearch] = useState("");
  const [endDateSearch, setEndDateSearch] = useState("");

  // ── Dialog montant facturé ────────────────────────────────────────────────
  const [montantOpen, setMontantOpen] = useState(false);
  const [trimestre, setTrimestre] = useState<any[]>([]);
  const [montantPrice, setMontantPrice] = useState<string | null>(null);
  const [loadmontantPrice, setLoadMontantPrice] = useState<boolean>(false);
  const [duration, setDuration] = useState<number | null>(null);
  const [clientCount, setClientCount] = useState<number | null>(null);
  const [montantDateFrom, setMontantDateFrom] = useState<string | null>(null);
  const [montantDateTo, setMontantDateTo] = useState<string | null>(null);
  // Commissions de la période renvoyées par l'API (frais enregistrés à la déclaration)
  const [montantFees, setMontantFees] = useState<number | null>(null);
  // Rémunération telle que facturée (part HT + TVA enregistrées) : un coach non assujetti ne reçoit pas la TVA
  const [montantRemuneration, setMontantRemuneration] = useState<string | null>(null);
  // Verified Trainer : prix total TTC de la période = rémunération + commissions enregistrées
  // (repli sur le taux de frais du coach tant que l'API ne renvoie pas les commissions)
  const montantPriceTtc = useMemo(() => {
    if (montantPrice === null || !isVerifiedTrainer()) return null;
    const net = Number(montantPrice);
    return (montantFees !== null ? net + montantFees : vtTtcFromNet(net, teachr)).toFixed(2);
  }, [montantPrice, montantFees, teachr]);

  // ── Dialog détail cours ───────────────────────────────────────────────────
  const [selectedCourse, setSelectedCourse] =
    useState<PunctualCourseDashboard | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // ── Accordion groupes ─────────────────────────────────────────────────────
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const toggleGroup = (idx: number) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  // ── Mise à jour des dates de filtre ──────────────────────────────────────
  useEffect(() => {
    if (!config.showDateFilter) return;
    const formatted: string[] = [];
    dateValues.forEach((date: any) => {
      formatted.push(moment(date?.toDate?.()).format("YYYY-MM-DD"));
    });
    setStartDateSearch(formatted[0] ? `${formatted[0]} 00:00:00` : "");
    setEndDateSearch(formatted[1] ? `${formatted[1]} 23:59:59` : "");
    setPage(0);
  }, [dateValues, config.showDateFilter]);

  // ── Calcul du montant facturé ─────────────────────────────────────────────
  useEffect(() => {
    if (!config.showMontant || !teachrId) return;
    setLoadMontantPrice(true);
    const formatted: string[] = [];
    const displayDates: string[] = [];
    trimestre.forEach((date: any) => {
      formatted.push(moment(date?.toDate?.()).format("YYYY-MM-DD"));
      displayDates.push(moment(date?.toDate?.()).format("DD MMMM YYYY"));
    });
    const start = formatted[0] ? `${formatted[0]} 00:00:00` : "";
    const end = formatted[1] ? `${formatted[1]} 23:59:59` : "";
    setMontantDateFrom(displayDates[0] ?? null);
    setMontantDateTo(displayDates[1] ?? null);
    if (start && end) {
      PunctualCourseRepository.getMontantFacture(teachrId, start, end)
        .then((res: any) => {
          setLoadMontantPrice(true);
          // L'API renvoie la rémunération de la période (teachr_price × heures) ; totalAmountVt n'existe plus.
          const amount = Number(res?.amount ?? 0);
          setMontantPrice(amount.toFixed(2));
          setMontantFees(res?.fees != null ? Number(res.fees) : null);
          setMontantRemuneration(res?.remuneration != null ? Number(res.remuneration).toFixed(2) : null);
          const duration = res?.duration ?? 0;
          setDuration(duration);
          setClientCount(res?.clientCount ?? 0);
          setLoadMontantPrice(false);
        })
        .catch(() => {
          setMontantPrice(null);
          setMontantFees(null);
          setMontantRemuneration(null);
          setClientCount(null);
          setLoadMontantPrice(false);
        });
    } else {
      setMontantPrice(null);
      setMontantFees(null);
      setMontantRemuneration(null);
      setClientCount(null);
       setLoadMontantPrice(false);
    }
  }, [trimestre, teachrId, config.showMontant]);

  // ── dataDispatch (utilisé par CourseRowActions) ───────────────────────────
  const dataDispatch = useMemo(
    () => ({
      [config.pageKey]: page,
      [config.limitKey]: rowsPerPage,
      isData: false,
      global: {
        teachr,
        nameSearch,
        subjectSearch: statusSearch,
        startDateSearch,
        endDateSearch,
      },
    }),
    [
      page,
      rowsPerPage,
      nameSearch,
      statusSearch,
      teachr,
      startDateSearch,
      endDateSearch,
      config.pageKey,
      config.limitKey,
    ],
  );

  // ── Fetch via useQuery ────────────────────────────────────────────────────
  const { data: courseData, isLoading } = useQuery({
    queryKey: [
      "courses",
      type,
      teachrId,
      page,
      rowsPerPage,
      nameSearch,
      statusSearch,
      startDateSearch,
      endDateSearch,
    ],
    queryFn: () => {
      switch (type) {
        case "historique":
          return PunctualCourseRepository.searchPunctualCoursePastByTeachrNew(
            page,
            teachrId!,
            nameSearch,
            statusSearch,
            startDateSearch,
            endDateSearch,
            rowsPerPage,
          );
        case "actif":
          return PunctualCourseRepository.getPunctualCourseByTeachr(
            teachrId!,
            page,
            rowsPerPage,
          );
        case "avenir":
          return PunctualCourseRepository.getPendingPunctualCourseByTeachr(
            teachrId!,
            page,
            rowsPerPage,
          );
        case "brouillon":
          return PunctualCourseRepository.getCourseNextMonth(
            teachrId!,
            page,
            rowsPerPage,
          );
      }
    },
    enabled: !!teachrId,
  });

  // ── Sync Redux ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (courseData !== undefined) {
      store.dispatch({ type: REDUX_TYPE_MAP[type], payload: courseData });
    }
  }, [courseData, type, deleteCourse]);

  // ── Données dérivées ──────────────────────────────────────────────────────
  const allCourses: any[] = useMemo(
    () => course?.punctualCourse?.[config.stateKey]?.courses ?? [],
    [config.stateKey, course?.punctualCourse],
  );
  const totalItems: number = courseData?.totalItems ?? 0;
  const isGrouped = type === "actif" || type === "brouillon";

  const courses = useMemo(() => {
    if (config.showDateFilter) return allCourses;
    if (!nameSearch) return allCourses;
    const q = nameSearch.toLowerCase();
    return allCourses.filter((item: any) => {
      const childName = `${item.forChildFirstName ?? ""} ${item.forChildLastName ?? ""}`.toLowerCase();
      const parentName = `${item.firstName ?? ""} ${item.lastName ?? ""}`.toLowerCase();
      return childName.includes(q) || parentName.includes(q);
    });
  }, [allCourses, nameSearch, config.showDateFilter]);

  const displayedCourses = useMemo(
    () => (limit && limit > 0 ? courses.slice(0, limit) : courses),
    [courses, limit],
  );

  const headCells: HeadCell[] = useMemo(() => {
    const cells: HeadCell[] = [
      {
        id: 1,
        numeric: false,
        disable: false,
        align: "left",
        label: `👤 ${capitalizeFirstLetter(getActiveClientBrandConfig())}`,
      },
      {
        id: 2,
        numeric: false,
        disable: false,
        align: "left",
        label: "📅 Date",
      },
      {
        id: 3,
        numeric: false,
        disable: false,
        align: "left",
        label: "⏱️ Durée",
      },
      {
        id: 4,
        numeric: false,
        disable: false,
        align: "center",
        // Verified Trainer : la colonne porte le prix total TTC, le détail est dans la fiche du cours
        label: isVerifiedTrainer() ? "💶 Prix total" : "💰 Rémunération",
      },
      {
        id: 5,
        numeric: false,
        disable: false,
        align: "center",
        label: "✅ Statut",
      },
      {
        id: 6,
        numeric: false,
        disable: false,
        align: "center",
        label: "📋 Format",
      },
    ];
    if (!limit)
      cells.push({
        id: 7,
        numeric: false,
        disable: false,
        align: "center",
        label: "⚙️ Actions",
      });
    return cells;
  }, [limit]);

  const hasActiveFilters = !!(nameSearch || statusSearch || dateValues.length);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleOpenDetail = (course: PunctualCourseDashboard) => {
    setSelectedCourse(course);
    setDetailOpen(true);
  };

  const clearFilters = () => {
    setNameSearch("");
    setStatusSearch("");
    setDateValues([]);
    setPage(0);
  };

  const resetMontant = () => {
    setTrimestre([]);
    setMontantPrice(null);
    setMontantFees(null);
    setMontantRemuneration(null);
    setDuration(null);
    setClientCount(null);
    setMontantDateFrom(null);
    setMontantDateTo(null);
  };

  const handleSubmitDelete = (
    coursId: number,
    clientId: number,
    setLoad: (value: React.SetStateAction<boolean>) => void,
    setOption: React.Dispatch<React.SetStateAction<OptionsProps>>,
  ) => {
    setLoad(true);
    setDeleteCourse(true);

    PunctualCourseRepository.deletePunctualByTeachrDashboard(coursId)
      .then((response) => {
        const res = response as { id: number; isCancelled: boolean };
        if (typeof res === "object") {
          setOption((p) => ({ ...p, deletes: false }));
          if (res?.id) {
            dispatch({
              type: DELETE_COURSE,
              payload: {
                id: coursId,
                clientId: clientId,
                courseType: REDUX_TYPE_MAP[type],
              },
            });
            store.dispatch(config.action(dataDispatch));
            dispatch({
              type: SHOW_SUCCESS,
              payload: `Votre ${getActivePrestationBrandConfig()} a bien été supprimé.`,
            });
            setLoad(false);
            setDeleteCourse(false);
          }
        }
      })
      .catch((err: any) => {
        setLoad(false);
        setDeleteCourse(false);
        setOption((p) => ({ ...p, deletes: false }));
        // P1 – garde serveur : le backend refuse la suppression d'un cours
        // mensuel dont des séances sont déjà passées
        const raw = `${err?.description ?? err?.message ?? err}`;
        if (raw.includes('course_transmitted_to_urssaf')) {
          dispatch({
            type: SHOW_ERROR,
            payload: `Impossible de supprimer : ${getActivePrestationBrandConfig("", "ce ", "cette ")} a déjà été transmis${getActivePrestationBrandConfig("", "", "e")} à l'URSSAF.`,
          });
        } else if (raw.includes('course_has_past_sessions')) {
          dispatch({
            type: SHOW_ERROR,
            payload: `Impossible de supprimer : des séances ont déjà été effectuées. Retirez les séances à venir depuis la fenêtre de modification.`,
          });
        } else {
          dispatch({
            type: SHOW_ERROR,
            payload: `La suppression ${getActivePrestationBrandConfig("", "du ", "de la ")} a échoué. Réessayez dans quelques instants.`,
          });
        }
      });
  };

  return {
    teachr,
    teachrId,
    config,
    // pagination
    page,
    rowsPerPage,
    // filtres
    showFilters,
    setShowFilters,
    nameSearch,
    setNameSearch,
    statusSearch,
    setStatusSearch,
    dateValues,
    setDateValues,
    // dates calculées
    startDateSearch,
    endDateSearch,
    // dialog montant
    montantOpen,
    setMontantOpen,
    trimestre,
    setTrimestre,
    montantPrice,
    montantPriceTtc,
    montantRemuneration,
    duration,
    clientCount,
    montantDateFrom,
    montantDateTo,
    // dialog détail
    selectedCourse,
    setSelectedCourse,
    detailOpen,
    setDetailOpen,
    // accordion
    expandedGroups,
    toggleGroup,
    // data
    isLoading,
    allCourses,
    totalItems,
    isGrouped,
    courses,
    displayedCourses,
    headCells,
    dataDispatch,
    hasActiveFilters,
    loadmontantPrice,
    // handlers
    handleChangePage,
    handleChangeRowsPerPage,
    handleOpenDetail,
    clearFilters,
    resetMontant,
    handleSubmitDelete,
  };
}
